<template>
  <div class="pre-approval-form">
    <el-form :model="form" label-width="140px">
      <!-- 产品名称 -->
      <el-form-item label="产品名称：" required>
        <el-select 
          v-model="form.name" 
          placeholder="请选择产品" 
          :disabled="isView"
          @change="handleProductChange"
          style="width: 180px;"
        >
          <el-option label="员工保" value="员工保" />
          <el-option label="货运险年保" value="货运险年保" />
        </el-select>
      </el-form-item>

      <!-- 保险公司 -->
      <el-form-item label="保险公司：" required>
        <el-select 
          v-model="form.insurance_type" 
          placeholder="请选择保险公司"
          :disabled="isView"
          filterable
          style="width: 180px;"
        >
          <el-option
            v-for="item in insuranceOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <!-- 禁止投保区域 -->
      <el-form-item label="禁止投保区域：" required>
        <div style="max-height: 200px; overflow-y: auto; border: 1px solid #dcdfe6; padding: 10px;">
          <el-tree
            ref="cityTree"
            :data="cityTreeData"
            show-checkbox
            :disabled="isView"
            node-key="id"
            :default-expand-all="false"
            :props="treeProps"
            @check="handleCityCheck"
          />
        </div>
      </el-form-item>

      <!-- 营业范围禁止承保关键词 (只有员工保才显示) -->
      <el-form-item v-if="form.name === '员工保'" label="营业范围禁止承保关键词：">
        <el-input
          type="textarea"
          v-model="form.business_scope_keywords"
          :disabled="isView"
          :rows="4"
          placeholder="请输入关键字，并用英文分号';'隔开"
          style="width: 300px;"
        />
      </el-form-item>

      <!-- 公司名称关键字 -->
      <el-form-item :label="keywordLabel">
        <el-input
          type="textarea"
          v-model="form.company_name_keywords"
          :disabled="isView"
          :rows="4"
          placeholder="请输入关键字，并用英文分号';'隔开"
          style="width: 300px;"
        />
      </el-form-item>
    </el-form>

    <!-- 按钮 -->
    <div class="form-buttons">
      <el-button v-if="!isView" type="primary" @click="handleSubmit" :loading="loading">保 存</el-button>
      <el-button @click="handleCancel">{{ isView ? '关 闭' : '取 消' }}</el-button>
    </div>
  </div>
</template>

<script>
import { savePreApproval, getPreApprovalInfo, getCityList, getInsuranceList, getInsuranceCompanyList } from '@/api/product'

export default {
  name: 'PreApprovalForm',
  props: {
    formData: {
      type: Object,
      default: null
    },
    isView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        id: null,
        name: '员工保',
        insurance_type: '',
        city_list: [],
        business_scope_keywords: '',
        company_name_keywords: ''
      },
      loading: false,
      cityTreeData: [],
      insuranceOptions: [],
      ygbInsuranceData: [], // 员工保保险公司数据
      hybInsuranceData: [], // 货运险年保保险公司数据
      treeProps: {
        children: 'children',
        label: 'text'
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.formData
    },
    keywordLabel() {
      return this.form.name === '员工保' ? '公司名称承保关键字：' : '公司名称禁止承保关键字：'
    }
  },
  async mounted() {
    await this.fetchInitialData()
    if (this.formData) {
      this.loadFormData()
    }
  },
  methods: {
    // 获取初始数据
    async fetchInitialData() {
      try {
        // 并行获取所有需要的数据
        const [cityResponse, ygbResponse, hybResponse] = await Promise.all([
          getCityList(),
          getInsuranceList(), // 员工保保险公司
          getInsuranceCompanyList() // 货运险年保保险公司
        ])

        // 处理城市数据
        if (cityResponse && cityResponse.err_code === 0) {
          this.cityTreeData = this.formatCityData(cityResponse.rows)
        }

        // 保存两种保险公司数据
        if (ygbResponse && ygbResponse.err_code === 0) {
          this.ygbInsuranceData = ygbResponse.rows || []
        }

        if (hybResponse && hybResponse.err_code === 0) {
          this.hybInsuranceData = hybResponse.rows || []
        }

        // 初始化保险公司选项
        this.updateInsuranceOptions()

      } catch (error) {
        console.error('获取初始数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },

    // 格式化城市数据
    formatCityData(cityData) {
      return cityData.map(province => ({
        id: province.id,
        text: province.province,
        province: province.province,
        children: province.children ? province.children.map(city => ({
          id: city.id,
          text: city.city,
          province: province.province,
          city: city.city,
          city_code: city.city_code
        })) : []
      }))
    },

    // 产品改变时更新保险公司选项
    handleProductChange() {
      this.form.insurance_type = ''
      this.updateInsuranceOptions()
    },

    // 更新保险公司选项
    updateInsuranceOptions() {
      if (this.form.name === '员工保') {
        this.insuranceOptions = this.ygbInsuranceData
      } else {
        this.insuranceOptions = this.hybInsuranceData
      }
    },

    // 处理城市选择
    handleCityCheck() {
      const checkedNodes = this.$refs.cityTree.getCheckedNodes(true)
      // 按照老项目的逻辑，只有具有id的城市节点才加入city_list，确保数据结构完全一致
      this.form.city_list = checkedNodes.filter(node => node.city && node.id).map(node => ({
        province: node.province,
        id: node.id,
        city: node.city
      }))
      console.log('城市选择结果:', this.form.city_list)
    },

    // 加载表单数据
    async loadFormData() {
      if (!this.formData.id) return

      try {
        const response = await getPreApprovalInfo({ id: this.formData.id })
        if (response && response.err_code === 0) {
          const data = response.data
          
          this.form.id = data.id
          this.form.name = data.name
          this.form.insurance_type = data.insurance_type

          // 处理关键字数据
          if (data.company_name_keywords) {
            try {
              const keywords = JSON.parse(data.company_name_keywords)
              this.form.company_name_keywords = Array.isArray(keywords) ? keywords.join(';') : data.company_name_keywords
            } catch (e) {
              this.form.company_name_keywords = data.company_name_keywords
            }
          }

          if (data.business_scope_keywords && data.business_scope_keywords !== 'null') {
            try {
              const keywords = JSON.parse(data.business_scope_keywords)
              this.form.business_scope_keywords = Array.isArray(keywords) ? keywords.join(';') : data.business_scope_keywords
            } catch (e) {
              this.form.business_scope_keywords = data.business_scope_keywords
            }
          }

          // 更新保险公司选项
          this.updateInsuranceOptions()

          // 设置城市选择
          if (data.city_list && data.city_list.length > 0) {
            this.form.city_list = data.city_list
            // 使用setTimeout确保树完全渲染后再设置选中状态
            setTimeout(() => {
              if (this.$refs.cityTree) {
                // 根据city_code查找并选中节点
                data.city_list.forEach(city => {
                  // 在树中查找匹配的城市节点
                  this.findAndCheckNode(this.cityTreeData, city)
                })
              }
            }, 200)
          }
        }
      } catch (error) {
        console.error('加载配置信息失败:', error)
        this.$message.error('加载配置信息失败')
      }
    },

    // 提交表单
    async handleSubmit() {
      // 验证必填项
      if (!this.form.name) {
        this.$message.warning('请选择产品')
        return
      }

      if (!this.form.insurance_type) {
        this.$message.warning('请选择保险公司')
        return
      }

      if (!this.form.city_list || this.form.city_list.length === 0) {
        this.$message.warning('请选择禁止投保区域')
        return
      }

      this.loading = true
      try {
        const data = {
          name: this.form.name,
          insurance_type: this.form.insurance_type,
          // 确保city_list的数据结构与老项目完全一致，只包含province、id、city三个字段
          city_list: this.form.city_list.map(city => ({
            province: city.province,
            id: city.id,
            city: city.city
          }))
        }

        if (this.form.id) {
          data.id = this.form.id
        }

        // 处理关键字
        if (this.form.business_scope_keywords) {
          data.business_scope_keywords = this.form.business_scope_keywords.split(';').filter(item => item.trim())
        }

        if (this.form.company_name_keywords) {
          data.company_name_keywords = this.form.company_name_keywords.split(';').filter(item => item.trim())
        }

        console.log('提交数据:', JSON.stringify(data, null, 2))

        const response = await savePreApproval(data)
        if (response && response.err_code === 0) {
          this.$emit('save', response.data)
        } else {
          this.$message.error(response?.err_msg || '保存失败')
        }
      } catch (error) {
        console.error('保存配置失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.loading = false
      }
    },

    // 取消
    handleCancel() {
      this.$emit('cancel')
    },

    // 递归查找并选中城市节点
    findAndCheckNode(treeData, targetCity) {
      for (let province of treeData) {
        if (province.children && province.children.length > 0) {
          for (let city of province.children) {
            // 优先根据city_code匹配
            if (targetCity.city_code && city.city_code && city.city_code === targetCity.city_code) {
              const node = this.$refs.cityTree.getNode(city.id)
              if (node) {
                this.$refs.cityTree.setChecked(node, true, false)
              }
              return true
            }
            // 其次根据id匹配
            if (city.id === targetCity.id) {
              const node = this.$refs.cityTree.getNode(city.id)
              if (node) {
                this.$refs.cityTree.setChecked(node, true, false)
              }
              return true
            }
            // 最后根据省市名称匹配
            if (city.province === targetCity.province && city.city === targetCity.city) {
              const node = this.$refs.cityTree.getNode(city.id)
              if (node) {
                this.$refs.cityTree.setChecked(node, true, false)
              }
              return true
            }
          }
        }
      }
      return false
    }
  }
}
</script>

<style scoped>
.pre-approval-form {
  padding: 20px 0;
}

.form-buttons {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.form-buttons .el-button {
  margin: 0 10px;
}
</style>